<template>
  <div id="screen_share_tile" class="relative rounded-lg overflow-hidden bg-[#0d0d1a] flex items-center justify-center w-full h-full">
    <video
      ref="videoRef"
      autoplay
      playsinline
      muted
      class="w-full h-full object-contain"
    />
    <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
      <span class="text-white text-xs font-medium">
        {{ isLocal ? 'You are screensharing' : `${displayName} is screensharing` }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useStore } from "vuex";
import { Track } from "livekit-client";
import { renderEntity } from "@/src/utils/utils.js";
import { livekitManager } from "@/src/services/chats/livekitManager.js";

const props = defineProps({
  userId: { type: [Number, String], required: true },
  isLocal: { type: Boolean, default: false },
});

const store = useStore();
const videoRef = ref(null);
let attachedTrack = null;
let retryTimer = null;

const allUsers = computed(() => store.getters["users/users"] || []);
const screenSharing = computed(() => store.getters["huddles/screenSharing"]);

const displayName = computed(() => {
  const user = allUsers.value.find((u) => Number(u.id) === Number(props.userId));
  if (user) return renderEntity(user);
  return `User ${props.userId}`;
});

const attachLocalScreen = () => {
  try {
    if (!videoRef.value) return false;
    const room = livekitManager.getRoom();
    if (!room) return false;

    const pub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    const track = pub?.track;
    if (track) {
      if (attachedTrack) {
        attachedTrack.detach(videoRef.value);
      }
      track.attach(videoRef.value);
      attachedTrack = track;
      return true;
    }
    return false;
  } catch (error) {
    console.error("ScreenShareTile: failed to attach local screen", error);
    return false;
  }
};

const attachRemoteScreen = () => {
  try {
    const room = livekitManager.getRoom();
    if (!room || !videoRef.value) return false;

    const participant = livekitManager.findRemoteParticipant(props.userId);
    if (!participant) return false;

    const pub = participant.getTrackPublication(Track.Source.ScreenShare);
    const track = pub?.track;
    if (track) {
      if (attachedTrack) attachedTrack.detach(videoRef.value);
      track.attach(videoRef.value);
      attachedTrack = track;
      return true;
    }
    return false;
  } catch (error) {
    console.error("ScreenShareTile: failed to attach remote screen", error);
    return false;
  }
};

const detachTrack = () => {
  try {
    if (attachedTrack && videoRef.value) {
      attachedTrack.detach(videoRef.value);
    }
    if (videoRef.value) {
      videoRef.value.srcObject = null;
    }
    attachedTrack = null;
  } catch (error) {
    console.error("ScreenShareTile: failed to detach track", error);
  }
};

const attachTrack = async () => {
  try {
    clearRetry();
    await nextTick();

    let success;
    if (props.isLocal) {
      success = attachLocalScreen();
    } else {
      success = attachRemoteScreen();
    }

    // If track not yet available (timing), retry a few times
    if (!success) {
      let attempts = 0;
      retryTimer = setInterval(() => {
        attempts++;
        let ok;
        if (props.isLocal) {
          ok = attachLocalScreen();
        } else {
          ok = attachRemoteScreen();
        }
        if (ok || attempts >= 10) {
          clearRetry();
        }
      }, 200);
    }
  } catch (error) {
    console.error("ScreenShareTile: failed to attach track", error);
  }
};

const clearRetry = () => {
  if (retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
};

watch(videoRef, (el) => { if (el) attachTrack(); });
watch(screenSharing, (val) => {
  if (val) {
    attachTrack();
  } else {
    detachTrack();
  }
});

const participants = computed(() => store.getters["huddles/participants"]);
watch(participants, () => {
  if (!props.isLocal) {
    const sharingParticipant = participants.value.find((p) => p.screen_sharing);
    if (sharingParticipant && String(sharingParticipant.user_id) === String(props.userId)) {
      attachTrack();
    } else if (!sharingParticipant) {
      detachTrack();
    }
  }
}, { deep: true });

onMounted(() => attachTrack());

onUnmounted(() => {
  try {
    clearRetry();
    detachTrack();
  } catch (error) {
    console.error("ScreenShareTile: failed to cleanup on unmount", error);
  }
});

defineExpose({ videoRef });
</script>
